import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, Check, X, Package, Image as ImageIcon, AlertTriangle, FileText, Download } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { BUSINESS, searchIcecat, searchExistingProducts, createProduct, updateProduct, uploadProductImage, getSuppliers, addSupplierPrice, getPricesForProduct, CATEGORY_LABELS } from '../../config';
import SourceComparisonTable from '../../components/admin/SourceComparisonTable';
import RepairabilityPanel from '../../components/admin/RepairabilityPanel';
import AIDescriptionPanel from '../../components/admin/AIDescriptionPanel';
import SupplierPricingPanel from '../../components/admin/SupplierPricingPanel';

const INITIAL_FORM = {
  name: '', category: 'pc', condition: 'reconditionne', grade: '',
  price: '', description: '', processor: '', ram: '', storage: '',
  screen_size: '', warranty: '6 mois', stock: 1, status: 'available',
};

// Detect if input is EAN/SKU (digits only, optionally with leading 0) or text search
function detectSearchType(value) {
  const cleaned = value.trim().replace(/[\s-]/g, '');
  return /^\d{4,}$/.test(cleaned) ? 'ean' : 'name';
}

// Find the best source with data for a given field
function bestSourceForField(sources, field) {
  if (!sources) return null;
  for (const key of ['ldlc', 'firecrawl', 'icecat']) {
    const src = sources[key];
    if (src?.found && src[field]) return { source: key, value: src[field] };
  }
  return null;
}

// Merge best data from all sources into a product form
function mergeSourcesIntoForm(sources) {
  const form = { ...INITIAL_FORM };
  const fields = ['name', 'processor', 'ram', 'storage', 'screen_size', 'price', 'description', 'category'];
  for (const f of fields) {
    const best = bestSourceForField(sources, f);
    if (best) form[f] = best.value;
  }
  return form;
}

// Get best image URL from sources
function getBestImage(sources) {
  if (!sources) return '';
  for (const key of ['ldlc', 'firecrawl', 'icecat']) {
    if (sources[key]?.image_url) return sources[key].image_url;
  }
  return '';
}

export default function AdminProductImport() {
  const navigate = useNavigate();

  const [step, setStep] = useState('search'); // 'search' | 'compare' | 'edit'
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [sources, setSources] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedImages, setSelectedImages] = useState([]); // multi-select
  const [videoUrls, setVideoUrls] = useState([]); // videos from Icecat
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Duplicate check
  const [existingProduct, setExistingProduct] = useState(null); // product already in catalog
  // Repairability index (Loi AGEC)
  const [repairability, setRepairability] = useState({
    documentation: 5, demontabilite: 5, pieces_dispo: 5, prix_pieces: 5, logiciel: 5,
  });
  const [repairabilityStatus, setRepairabilityStatus] = useState(null);
  // Suppliers pricing
  const [suppliers, setSuppliers] = useState([]);
  const [supplierPrices, setSupplierPrices] = useState([]); // { supplier_id, supplier_name, price_ht, ... }
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [marginPct, setMarginPct] = useState(25);
  const [manualPriceHT, setManualPriceHT] = useState('');

  // Load suppliers on mount
  const loadSuppliers = useCallback(async () => {
    try {
      const data = await getSuppliers();
      setSuppliers((data.suppliers || []).filter(s => s.active));
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const detectedType = useMemo(() => detectSearchType(searchValue), [searchValue]);
  const anySourceFound = sources && Object.values(sources).some(s => s?.found);

  const handleSearch = async (e) => {
    e.preventDefault();
    const val = searchValue.trim();
    if (!val) return;
    const type = detectSearchType(val);
    setSearching(true);
    setSearchError('');
    setSources(null);
    try {
      const data = await searchIcecat(type, val);
      if (data.sources) {
        // Multi-source response (N8N workflow v2)
        setSources(data.sources);
        const found = Object.values(data.sources).some(s => s?.found);
        if (found) {
          const merged = mergeSourcesIntoForm(data.sources);
          setForm(merged);
          setImageUrl(getBestImage(data.sources));
          // Check if product already exists in catalog
          try {
            const existing = await searchExistingProducts(merged.name.split(' ').slice(0, 3).join(' '));
            if (existing?.products?.length > 0) setExistingProduct(existing.products[0]);
            else setExistingProduct(null);
          } catch { setExistingProduct(null); }
          setStep('compare');
        } else {
          setSearchError(
            type === 'ean'
              ? 'Aucun produit trouve pour cet EAN. Verifiez le code-barres ou essayez une recherche par nom (ex: Apple MacBook Air M4).'
              : 'Aucun produit trouve pour "' + val + '". Essayez avec la marque suivie de la reference exacte (ex: APC BX750MI-FR).'
          );
        }
      } else if (data.product) {
        // Legacy single-source response
        const legacySources = { icecat: { ...data.product, found: data.product.icecat_found, source: 'icecat' } };
        setSources(legacySources);
        if (data.product.icecat_found) {
          prefillForm(data.product);
          setStep('compare');
        } else {
          setSearchError('Aucun produit trouve.');
        }
      }
    } catch (err) {
      setSearchError(err.message || 'Erreur de connexion au service de recherche.');
    }
    setSearching(false);
  };

  const prefillForm = (product) => {
    setForm({
      name: product.name || '', category: product.category || 'pc',
      condition: product.condition || 'reconditionne', grade: product.grade || '',
      price: product.price?.toString() || '', description: product.description || '',
      processor: product.processor || '', ram: product.ram || '',
      storage: product.storage || '', screen_size: product.screen_size || '',
      warranty: product.warranty || '6 mois', stock: product.stock || 1, status: 'available',
    });
    setImageUrl(product.image_url || '');
  };

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const repairScore = useMemo(() => {
    const vals = Object.values(repairability);
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }, [repairability]);

  const handleSave = async (e, mode = 'create') => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 1,
        grade: form.grade || null, processor: form.processor || null,
        ram: form.ram || null, storage: form.storage || null, screen_size: form.screen_size || null,
        repairability_scores: repairability,
        repairability_status: repairabilityStatus,
        video_urls: videoUrls,
      };

      let productId;
      if (mode === 'update' && existingProduct) {
        await updateProduct(existingProduct.id, payload);
        productId = existingProduct.id;
      } else {
        const newProduct = await createProduct(payload);
        productId = newProduct.id;
      }

      // Upload all selected images (or fallback to single imageUrl)
      const imagesToUpload = selectedImages.length > 0 ? selectedImages : (imageUrl ? [imageUrl] : []);
      for (const imgUrl of imagesToUpload.slice(0, 5)) {
        try {
          const imgResp = await fetch(imgUrl);
          const blob = await imgResp.blob();
          const ext = imgUrl.split('.').pop()?.split('?')[0] || 'jpg';
          const file = new File([blob], `product-image-${Date.now()}.${ext}`, { type: blob.type || 'image/jpeg' });
          await uploadProductImage(productId, file);
        } catch { /* non-blocking */ }
      }

      navigate(`/my/produits/${productId}/modifier`);
    } catch (err) {
      setSaveError(err.message || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const resetSearch = () => {
    setStep('search');
    setSearchValue('');
    setSearchError('');
    setSources(null);
    setForm(INITIAL_FORM);
    setImageUrl('');
    setSelectedImages([]);
    setVideoUrls([]);
    setSaveError('');
    setExistingProduct(null);
    setRepairability({ documentation: 5, demontabilite: 5, pieces_dispo: 5, prix_pieces: 5, logiciel: 5 });
    setRepairabilityStatus(null);
  };

  // ── PDF Generation (opens print-ready page) ──
  const openPrintPage = (html) => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 800);
  };

  const generateProductPDF = () => {
    const today = new Date().toLocaleDateString('fr-FR');
    const condLabel = form.condition === 'reconditionne' ? 'Reconditionne' : 'Neuf';
    const gradeLabel = form.grade ? ' - Grade ' + form.grade : '';
    const specs = [
      form.processor ? ['Processeur', form.processor] : null,
      form.ram ? ['RAM', form.ram] : null,
      form.storage ? ['Stockage', form.storage] : null,
      form.screen_size ? ['Ecran', form.screen_size] : null,
      ['Garantie', form.warranty],
      ['Etat', condLabel + gradeLabel],
    ].filter(Boolean);
    const specRows = specs.map(([k, v]) =>
      '<tr><td style="font-weight:bold;color:#555;padding:9px 12px;border:1px solid #e0e0e0;width:140px">' + k + '</td><td style="padding:9px 12px;border:1px solid #e0e0e0">' + v + '</td></tr>'
    ).join('');
    const mainImg = selectedImages.length > 0 ? selectedImages[0] : imageUrl;
    const imgBlock = mainImg
      ? '<img src="' + mainImg + '" style="width:200px;height:200px;object-fit:contain;border:1px solid #eee;border-radius:8px;padding:8px" crossorigin="anonymous">'
      : '<div style="width:200px;height:200px;background:#f5f5f5;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#bbb;font-size:12px">Pas d\'image</div>';
    const extraImgs = selectedImages.length > 1
      ? '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px">' + selectedImages.slice(1, 5).map(u =>
          '<img src="' + u + '" style="width:90px;height:90px;object-fit:contain;border:1px solid #eee;border-radius:6px;padding:4px" crossorigin="anonymous">'
        ).join('') + '</div>'
      : '';
    const descBlock = form.description
      ? '<div style="background:#f8f9fa;padding:15px;border-radius:8px;font-size:13px;color:#555;line-height:1.6;margin-bottom:20px">' + form.description + '</div>'
      : '';
    const html = '<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Fiche Produit - ' + form.name + '</title>'
      + '<style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;color:#333;padding:30px;background:white}'
      + '.header{background:linear-gradient(135deg,#1a1a2e,#16213e);color:white;padding:25px;border-radius:10px;margin-bottom:25px}'
      + '.header-sub{font-size:13px;opacity:0.8;margin-bottom:6px}.product-name{font-size:22px;font-weight:bold}'
      + '.badges{margin-top:10px;display:flex;gap:8px}.badge{padding:3px 12px;border-radius:20px;font-size:12px;font-weight:bold;background:rgba(255,255,255,0.25)}'
      + '.content{display:flex;gap:25px;margin-bottom:20px}.price{font-size:38px;font-weight:bold;color:#d4a017}'
      + '.price-sub{color:#888;font-size:13px;margin-bottom:16px}table{width:100%;border-collapse:collapse;font-size:14px}'
      + 'table tr:nth-child(even){background:#f8f9fa}.footer{text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:15px;margin-top:20px}'
      + '@media print{body{padding:20px}}</style></head><body>'
      + '<div class="header"><div class="header-sub">Atelier Météris - boutique.meteris.fr</div>'
      + '<div class="product-name">' + form.name + '</div>'
      + '<div class="badges"><span class="badge">' + condLabel + '</span>' + (form.grade ? '<span class="badge">Grade ' + form.grade + '</span>' : '') + '</div></div>'
      + '<div class="content"><div>' + imgBlock + extraImgs + '</div><div style="flex:1">'
      + '<div class="price">' + (form.price ? form.price + ' EUR' : 'Prix sur demande') + '</div>'
      + '<div class="price-sub">Prix TTC - Garantie ' + form.warranty + '</div>'
      + '<table>' + specRows + '</table></div></div>' + descBlock
      + '<div class="footer">Fiche produit generee le ' + today + ' - Atelier Météris - 8 Rue Georges Claude, 77100 Meaux - ' + BUSINESS.telephone + '</div>'
      + '</body></html>';
    openPrintPage(html);
  };

  return (
    <AdminLayout title="Importer un produit">
      <div className="max-w-4xl">
        <button onClick={() => navigate('/my/produits')}
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-secondary transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour aux produits
        </button>

        {/* ── SEARCH ── */}
        <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
          <h3 className="font-heading font-semibold text-white mb-1">Recherche produit</h3>
          <p className="text-xs text-slate-500 mb-4">
            Tapez un EAN / code-barres (chiffres) ou un nom de produit (Marque + Reference).
            La recherche interroge Icecat, LDLC et Firecrawl en parallele.
          </p>

          <div className="flex gap-3 mb-4">
            {[
              { value: 'reconditionne', label: 'Reconditionne', icon: '♻️', sub: 'Icecat, LDLC, Firecrawl' },
              { value: 'neuf', label: 'Neuf', icon: '🏷️', sub: 'Grossistes (a configurer)' },
            ].map(opt => (
              <button key={opt.value} type="button"
                onClick={() => handleChange('condition', opt.value)}
                className={`flex-1 p-3 rounded-sm border-2 text-center transition-colors ${
                  form.condition === opt.value
                    ? 'border-secondary/50 bg-secondary/5'
                    : 'border-slate-200 bg-white/[0.02] hover:border-slate-300'
                }`}>
                <div className={`text-sm font-semibold ${form.condition === opt.value ? 'text-secondary' : 'text-slate-600'}`}>
                  {opt.icon} {opt.label}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">{opt.sub}</div>
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                placeholder="Ex: 0731304406112 ou Apple MacBook Air M4"
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 focus:border-secondary outline-none pr-32"
                required
                minLength={2}
              />
              {searchValue.trim().length >= 2 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  {detectedType === 'ean' ? 'EAN / Code-barres' : 'Marque + Reference'}
                </span>
              )}
            </div>

            <button type="submit" disabled={searching}
              className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-6 py-3 text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center gap-2 justify-center shrink-0">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Rechercher
            </button>
          </form>

          {searchError && (
            <div className="mt-3 text-sm bg-rose-50 border border-rose-200 rounded-sm px-4 py-3">
              <p className="text-rose-600 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {searchError}
              </p>
              <button type="button"
                onClick={() => { setForm({ ...INITIAL_FORM, name: searchValue }); setStep('edit'); }}
                className="mt-2 text-xs text-secondary hover:text-secondary underline">
                Creer le produit manuellement
              </button>
            </div>
          )}
        </div>

        {/* ── COMPARE SOURCES ── */}
        {step === 'compare' && sources && (
          <div className="space-y-6 mb-6">
            <SourceComparisonTable
              sources={sources}
              selectedValues={form}
              onValueChange={handleChange}
            />

            {/* Grade + Warranty selectors */}
            <div className="bg-white rounded-sm border border-slate-200 p-5">
              <h4 className="font-heading font-semibold text-white text-sm mb-3">Etat et garantie</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Grade</label>
                  <select value={form.grade} onChange={e => handleChange('grade', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none">
                    <option value="">Aucun</option>
                    <option value="A">Grade A</option>
                    <option value="B">Grade B</option>
                    <option value="C">Grade C</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Garantie</label>
                  <select value={form.warranty} onChange={e => handleChange('warranty', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none">
                    <option value="3 mois">3 mois</option>
                    <option value="6 mois">6 mois</option>
                    <option value="12 mois">12 mois</option>
                    <option value="24 mois constructeur">24 mois constructeur</option>
                  </select>
                </div>
              </div>
            </div>

            {/* All images from sources — multi-select */}
            {(() => {
              const allImages = [];
              const seenUrls = new Set();
              Object.entries(sources).forEach(([key, src]) => {
                const imgs = src?.images || (src?.image_url ? [src.image_url] : []);
                imgs.forEach(u => { if (u && !seenUrls.has(u)) { seenUrls.add(u); allImages.push({ url: u, source: src?.source_label || key }); } });
              });
              // Collect videos
              const allVideos = [];
              Object.entries(sources).forEach(([key, src]) => {
                if (src?.videos) src.videos.forEach(v => allVideos.push({ ...v, source: src?.source_label || key }));
              });
              if (allVideos.length > 0 && videoUrls.length === 0) {
                // Auto-select all videos on first render
                setTimeout(() => setVideoUrls(allVideos.map(v => v.url)), 0);
              }
              return (
                <>
                  {allImages.length > 0 && (
                    <div className="bg-white rounded-sm border border-slate-200 p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs text-slate-600 font-medium">Images disponibles ({allImages.length})</h4>
                        <span className="text-[10px] text-secondary">{selectedImages.length} selectionnee{selectedImages.length > 1 ? 's' : ''} (max 5)</span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {allImages.map((img, i) => {
                          const isSelected = selectedImages.includes(img.url);
                          return (
                            <button key={i} type="button" onClick={() => {
                              setSelectedImages(prev => {
                                if (isSelected) return prev.filter(u => u !== img.url);
                                if (prev.length >= 5) return prev;
                                return [...prev, img.url];
                              });
                              if (!imageUrl) setImageUrl(img.url);
                            }}
                              className={`relative w-20 h-20 rounded-sm border-2 p-1.5 transition-colors ${isSelected ? 'border-secondary bg-secondary/10' : 'border-slate-200 bg-slate-100 hover:border-slate-300'}`}>
                              <img src={img.url} alt="" className="w-full h-full object-contain rounded-sm" />
                              <span className="absolute -top-1 -right-1 text-[8px] px-1 py-0.5 rounded bg-black/80 text-slate-600">{img.source}</span>
                              {isSelected && (
                                <span className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-secondary flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-black" />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {allVideos.length > 0 && (
                    <div className="bg-white rounded-sm border border-slate-200 p-4 space-y-2">
                      <h4 className="text-xs text-slate-600 font-medium">Videos disponibles ({allVideos.length})</h4>
                      <div className="space-y-2">
                        {allVideos.map((vid, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 rounded-sm border border-slate-200">
                            <video src={vid.url} className="w-32 h-20 rounded object-cover bg-black" muted preload="metadata" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">{vid.description || 'Video produit'}</p>
                              <p className="text-[10px] text-slate-500">Source: {vid.source}</p>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={videoUrls.includes(vid.url)}
                                onChange={e => setVideoUrls(prev => e.target.checked ? [...prev, vid.url] : prev.filter(u => u !== vid.url))}
                                className="accent-secondary" />
                              <span className="text-xs text-slate-600">Inclure</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            <RepairabilityPanel
              scores={repairability}
              onScoresChange={setRepairability}
              status={repairabilityStatus}
              onStatusChange={setRepairabilityStatus}
              productName={form.name}
              compact
            />

            <AIDescriptionPanel
              productData={form}
              currentDescription={form.description}
              onDescriptionAccepted={(desc) => handleChange('description', desc)}
            />

            {/* Duplicate warning */}
            {existingProduct && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-700 font-medium">Produit similaire deja en catalogue</p>
                  <p className="text-xs text-amber-600/80 mt-1">
                    "{existingProduct.name}" (ID: {existingProduct.id}) — {existingProduct.status === 'available' ? 'Disponible' : existingProduct.status}
                    {existingProduct.price ? ` — ${existingProduct.price} €` : ''}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">Vous pourrez choisir de mettre a jour ce produit ou d'en creer un nouveau.</p>
                </div>
              </div>
            )}

            <SupplierPricingPanel
              suppliers={suppliers}
              supplierPrices={supplierPrices}
              onSupplierPricesChange={setSupplierPrices}
              selectedSupplier={selectedSupplier}
              onSelectedSupplierChange={setSelectedSupplier}
              marginPct={marginPct}
              onMarginChange={setMarginPct}
              manualPriceHT={manualPriceHT}
              onManualPriceChange={setManualPriceHT}
              onApplyPrice={(price) => handleChange('price', price)}
            />

            {/* Action buttons + PDF downloads */}
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setStep('edit')}
                className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-6 py-2.5 text-sm hover:bg-secondary transition-colors">
                Valider et completer
              </button>
              <button type="button" onClick={generateProductPDF}
                className="inline-flex items-center gap-1.5 bg-slate-100 text-primary font-medium rounded-full px-5 py-2.5 text-sm hover:bg-white/20 transition-colors">
                <FileText className="w-4 h-4" /> Fiche produit PDF
              </button>
              <button onClick={resetSearch}
                className="bg-slate-100 text-primary font-medium rounded-full px-5 py-2.5 text-sm hover:bg-white/20 transition-colors">
                Nouvelle recherche
              </button>
            </div>
          </div>
        )}

        {/* ── EDIT FORM ── */}
        {step === 'edit' && (
          <form onSubmit={e => handleSave(e, existingProduct ? 'update' : 'create')} className="space-y-6">
            <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-4">
              <h3 className="font-heading font-semibold text-primary">Informations generales</h3>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Nom du produit</label>
                <input type="text" required value={form.name} onChange={e => handleChange('name', e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 focus:border-secondary outline-none"
                  placeholder="Ex: Apple MacBook Air M4 13 pouces" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Categorie</label>
                  <select value={form.category} onChange={e => handleChange('category', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none">
                    {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                      <option key={slug} value={slug}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Etat</label>
                  <select value={form.condition} onChange={e => handleChange('condition', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none">
                    <option value="reconditionne">Reconditionne</option><option value="neuf">Neuf</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Grade</label>
                  <select value={form.grade} onChange={e => handleChange('grade', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none">
                    <option value="">Aucun</option><option value="A">Grade A</option><option value="B">Grade B</option><option value="C">Grade C</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Prix TTC</label>
                  <input type="number" step="0.01" required value={form.price} onChange={e => handleChange('price', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none" placeholder="299.00" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Stock</label>
                  <input type="number" min="0" value={form.stock} onChange={e => handleChange('stock', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1.5">Description</label>
                <textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={3}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none resize-none" placeholder="Description du produit..." />
              </div>
            </div>

            <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-4">
              <h3 className="font-heading font-semibold text-primary">Caracteristiques</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Processeur</label>
                  <input type="text" value={form.processor} onChange={e => handleChange('processor', e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none" placeholder="Intel Core i5-1145G7" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">RAM</label>
                  <input type="text" value={form.ram} onChange={e => handleChange('ram', e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none" placeholder="16 Go DDR4" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Stockage</label>
                  <input type="text" value={form.storage} onChange={e => handleChange('storage', e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none" placeholder="SSD 256 Go" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Taille ecran</label>
                  <input type="text" value={form.screen_size} onChange={e => handleChange('screen_size', e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none" placeholder="15.6 pouces Full HD" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1.5">Garantie</label>
                  <select value={form.warranty} onChange={e => handleChange('warranty', e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none">
                    <option value="3 mois">3 mois</option><option value="6 mois">6 mois</option>
                    <option value="12 mois">12 mois</option><option value="24 mois constructeur">24 mois constructeur</option>
                  </select>
                </div>
              </div>
            </div>

            <RepairabilityPanel
              scores={repairability}
              onScoresChange={setRepairability}
              status={repairabilityStatus}
              onStatusChange={setRepairabilityStatus}
              productName={form.name}
            />

            {imageUrl && (
              <div className="bg-white rounded-sm border border-slate-200 p-6 space-y-3">
                <h3 className="font-heading font-semibold text-primary">Image</h3>
                <div className="flex items-center gap-4">
                  <img src={imageUrl} alt="" className="w-24 h-24 object-contain rounded-sm bg-slate-100 p-2 border border-slate-200" />
                  <div>
                    <p className="text-xs text-slate-600">Sera telechargee et ajoutee au produit.</p>
                    <button type="button" onClick={() => setImageUrl('')} className="text-xs text-rose-600 hover:text-rose-700 mt-1">Retirer</button>
                  </div>
                </div>
              </div>
            )}

            {saveError && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-sm px-4 py-3">{saveError}</div>
            )}

            {/* Duplicate warning in edit mode */}
            {existingProduct && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
                <p className="text-sm text-amber-700">Produit existant detecte : "{existingProduct.name}"</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              {existingProduct ? (
                <>
                  <button type="button" disabled={saving} onClick={e => handleSave(e, 'update')}
                    className="bg-blue-600 text-white font-semibold rounded-full px-8 py-3 text-sm hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center gap-2">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />} Mettre a jour le produit existant
                  </button>
                  <button type="button" disabled={saving} onClick={e => handleSave(e, 'create')}
                    className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-8 py-3 text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center gap-2">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />} Creer un nouveau produit
                  </button>
                </>
              ) : (
                <button type="button" disabled={saving} onClick={e => handleSave(e, 'create')}
                  className="bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-8 py-3 text-sm hover:bg-secondary transition-colors disabled:opacity-50 flex items-center gap-2">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Creer le produit
                </button>
              )}
              {anySourceFound && (
                <button type="button" onClick={() => setStep('compare')}
                  className="bg-slate-100 text-primary font-medium rounded-full px-6 py-3 text-sm hover:bg-white/20 transition-colors">
                  Retour au comparatif
                </button>
              )}
              <button type="button" onClick={resetSearch}
                className="bg-slate-100 text-primary font-medium rounded-full px-6 py-3 text-sm hover:bg-white/20 transition-colors">
                Nouvelle recherche
              </button>
            </div>
          </form>
        )}
      </div>
    </AdminLayout>
  );
}
